import { BranchCode } from "api/risk";
import { GenericMessage } from "components/feedback/Message"
import { EyeIcon } from "components/icons/Icons";
import { Sizes } from "components/forms/NumberSelector";
import { GenericOperator } from "enums/genericOperator";
import { NsKey } from "enums/NsKey";
import { useGenericSurvey } from "features/subscription-process/profiling-process/hooks/useGenericSurvey";
import { EligibilityRule, PossibleAnswer } from "features/subscription-process/profiling-process/types/interfaces";
import { useEligibilityCheck } from "features/subscription-process/home-insurance/hooks/useEligibilityCheck";
import { useDialog } from "hooks/useDialog";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { FC, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { realEstateAPI } from "services/swagger-api/realEstateAPI";
import { RealEstateEligibilityDetails } from "./RealEstateEligibilityDetails";

interface RealEstateEligibilityProps {
  realEstateId: string
  onEligibilityChange: (eligibility: boolean) => void
  branchCode: BranchCode
  refreshKey?: number
}

export const cbnoEligibilityRules: EligibilityRule[] = [
  {
    key: "local_commercial",
    constraints: [
      { questionKey: "typeBien", operator: GenericOperator.Equals, value: 4 }
    ],
    result: "non-eligible",
    reason: "local_commercial",
    message: "Les locaux commerciaux ne sont pas éligibles à la CBNO"
  },
  {
    key: "residence_principale_secondaire",
    constraints: [
      { questionKey: "typeResidence", operator: GenericOperator.In, values: ["Principale", "Secondaire"] }
    ],
    result: "non-eligible",
    reason: "residence_type",
    message: "La CBNO est réservée aux investissements locatifs"
  },
  {
    key: "ehpad_tourisme",
    constraints: [
      { questionKey: "situation", operator: GenericOperator.In, values: ["Service", "Tourisme"] }
    ],
    result: "non-eligible",
    reason: "situation",
    message: "Le produit CBNO ne permet pas la souscription en EHPAD ou en résidence de tourisme"
  }
];

export const RealEstateEligibility: FC<RealEstateEligibilityProps> = ({ realEstateId, onEligibilityChange, branchCode, refreshKey }) => {
  const [surveyForm, setSurveyForm] = useState<Record<string, PossibleAnswer> | undefined>();
  const { mapToSurveyFormValue } = useGenericSurvey()
  const { t } = useDynamicTranslation([NsKey.RealEstate, NsKey.Common])
  const { openDialog } = useDialog()

  // Verdict d'éligibilité calculé au back (source unique).
  const { verdict } = useEligibilityCheck(branchCode, realEstateId, { refreshKey })
  const isEligible = verdict?.isEligible ?? false

  // Réponses du questionnaire conservées uniquement pour le détail « Voir pourquoi ».
  useEffect(() => {
    const getRealEstate = async () => {
      if (!realEstateId) return;
      try {
        const response = await realEstateAPI.getRealEstateById({
          bienId: realEstateId,
          includeQuestionnaire: branchCode === BranchCode.UPI,
          codeBranche: branchCode
        });
        if (response) {
          if (response.surveyAnswers) {
            setSurveyForm(mapToSurveyFormValue(response.surveyAnswers));
          }
        }
      } catch (error: any) {
        console.error("Error fetching real estate:", error);
        toast.error(error?.response?.error?.message || "Error loading real estate data");
      }
    };

    getRealEstate();
  }, [realEstateId, mapToSurveyFormValue, branchCode, refreshKey]);

  const onSeeWhy = useCallback(() => {
    openDialog({
      title: t('REAL_ESTATE.SECTIONS.UPI_ELIGIBILITY_CRITERIA'),
      size: Sizes["2xl"],
    },
      <RealEstateEligibilityDetails surveyForm={surveyForm} />
    )
  }, [openDialog, surveyForm, t])

  useEffect(() => {
    if (realEstateId && verdict) {
      onEligibilityChange(verdict.isEligible)
    }
  }, [realEstateId, verdict, onEligibilityChange])

  if (realEstateId && !verdict) {
    return null
  }

  if (realEstateId && !isEligible) {
    return <GenericMessage
      type="error"
      dismissible={false}
      message={t("REAL_ESTATE.MESSAGES.UPI_NOT_ELIGIBLE")}
      actionButton={
        {
          label: t("COMMON.SEE_WHY"),
          icon: EyeIcon,
          onClick: onSeeWhy
        }
      }
    />
  }

  if (realEstateId && isEligible) {
    return <GenericMessage
      type="success"
      dismissible={false}
      message={t("REAL_ESTATE.MESSAGES.UPI_ELIGIBLE")}
      actionButton={
        {
          label: t("COMMON.SEE_WHY"),
          icon: EyeIcon,
          onClick: onSeeWhy
        }
      }
    />
  }

  return <GenericMessage
    type="info"
    dismissible={false}
    message={t("REAL_ESTATE.MESSAGES.SELECT_REAL_ESTATE")}
  />
}
